/**
 * A source: anything the person hands OS while setting up (a file, a photo, a
 * pasted table, a link of any kind, a link said on a call). Each is one row of
 * `app.agent_file` with the setup session it belongs to, read on its own the
 * moment it is added, and it hands the harness typed findings, each with where
 * it was seen; one bad source never holds back another.
 *
 * A source plays one of three roles: `mine` (the person's own business: every
 * finding counts and its rows can come in), `like` (one they like the shape of:
 * only its shape counts, never its facts or rows) and `reference` (an app they
 * use today, never named back: the `like` set). Its state, in the words the
 * conversation's column shows: waiting (a heard link not read yet), uploading,
 * reading, needs (the person must say something first), read, partly (read in
 * part, with how much and the way on), failed (with why and the way on) and
 * left (the person chose to go on without it).
 */
import type { AgentFile, FileFailure, FileKind } from './agent-files';
import type { FormulaNode } from './formula';
import type { SheetsLeftPart } from './left-out-line';
import { STUDIO_SOURCE_ROLES, type StudioImport } from './setup-draft/draft';
import type { WebsiteFailure } from './websites';

/** What kind of link a source came from. */
export const LINK_KINDS = ['website', 'maps', 'sheet', 'drive', 'table', 'social', 'listing', 'store', 'catalogue', 'pdf', 'video', 'help'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

/** Mine · Like this · Reference app: the studio draft's own list, under the name source code reads. */
export const SOURCE_ROLES = STUDIO_SOURCE_ROLES;
export type SourceRole = (typeof SOURCE_ROLES)[number];

/** The state a source shows, one `setup.studio.source.state.*` word each. */
export const SOURCE_STATES = ['waiting', 'uploading', 'reading', 'needs', 'read', 'partly', 'failed', 'left'] as const;
export type SourceState = (typeof SOURCE_STATES)[number];

/** What a finding says. */
export const FINDING_KINDS = [
  'business_fact',
  'thing_to_keep',
  'field',
  'link',
  'number_watched',
  'money_flow',
  'vocabulary',
  'people_and_roles',
  'sample_rows',
  'importable_data',
  'question',
  'conflict',
  'cannot_do',
] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];

/** Where a finding was seen: at least one key; a cell is `sheet + column + row`. */
export type FindingWhere = {
  readonly sheet?: string;
  readonly column?: string;
  readonly row?: number;
  readonly page?: number;
  readonly line?: number;
  readonly url?: string;
};

type FindingBase<K extends FindingKind> = {
  /** `f1`, `f2` … within its source. */
  readonly id: string;
  readonly kind: K;
  /** One plain sentence, up to 200 characters, never a column list. */
  readonly text: string;
  readonly where: FindingWhere;
  readonly sure: 'read' | 'guess';
  /** 0 to 1. */
  readonly confidence: number;
};

export type BusinessFactAbout = 'name' | 'phone' | 'email' | 'address' | 'area' | 'hours' | 'gstin' | 'category' | 'delivery' | 'licence' | 'other';

/**
 * A column of a record candidate a workbook map found (step 3.1 of
 * docs/plans/workbook-map.md), as setup draws it: typed, a choice, a pick of
 * the list the map found in the same column (`list`), or worked out
 * (`formula`, with its result and its tree, each `field` node's `fieldId` a
 * column letter of the same sheet: setup swaps each for the field's key).
 */
export type FindingColumn = {
  readonly letter: string;
  readonly name: string;
  readonly type: 'text' | 'number' | 'money' | 'date' | 'boolean' | 'select' | 'list' | 'formula' | 'series';
  readonly choices?: readonly string[];
  /**
   * A number series' (8.8 of docs/plans/workbook-map.md): the letter of the
   * column each value of which counts its own numbers (Model, so CASE and JCB
   * each have theirs), or null for one count.
   */
  readonly per?: string | null;
  readonly result?: 'money' | 'number';
  readonly expr?: FormulaNode;
  /** Shown only when the choice of column `letter` (a `select` of the same candidate) is `equals`: the file fills it only then. */
  readonly shownWhen?: { readonly letter: string; readonly equals: string };
  /** A money column's few amounts, smallest first: the quick amounts of its field. */
  readonly amounts?: readonly number[];
  /**
   * A column of another kind that shares rows with this one, brought along
   * because a formula of this kind reads it: Received on a job row is what was
   * paid on the job, and the later payments' Received is the other kind's. A
   * total of it, or of a formula over it, is the sheet's only across both.
   */
  readonly beside?: true;
};

/** A summary's condition on a total, by the data sheet's column name. */
export type FindingWhen = { readonly column: string; readonly equals: string };

/** One typed thing a source taught, with where it was seen. */
export type SourceFinding =
  | (FindingBase<'business_fact'> & { readonly about: BusinessFactAbout; readonly value: string })
  | (FindingBase<'thing_to_keep'> & {
      readonly name: string;
      readonly shape: 'record' | 'list';
      /** A workbook map's: the sheet, and the kind of rows (`k1`) a record candidate is. */
      readonly sheet?: string;
      readonly rowKind?: string;
      /**
       * A kind the file keeps out of its profit (W24): a value that sets it
       * apart, which a profit formula skips (Office Jama). Its rows move money
       * from one place of the business to another (L36 of docs/plans/ledger.md).
       */
      readonly apart?: true;
      /** A record candidate's columns, as setup draws them (3.1). */
      readonly columns?: readonly FindingColumn[];
      /** A list candidate's column, by letter, and its names, cleaned: the rows it may start with (W9). */
      readonly letter?: string;
      readonly values?: readonly string[];
      /**
       * The figures the file totals by this list's names (a pasted pivot's Pending by customer, W12): each
       * a worked-out column of the same sheet, by letter, and the pivot's sheet ("Payment Pending List"). Setup
       * works each out on the list from the records, and keeps the pivot as a saved view of the list (6.7).
       */
      readonly totals?: readonly { readonly name: string; readonly letter: string; readonly sheet?: string }[];
    })
  | (FindingBase<'field'> & {
      readonly thing: string;
      readonly name: string;
      /** A field type id the manifest knows. */
      readonly type: string;
      readonly choices?: readonly string[];
      readonly sample?: string;
      /** A workbook map's column letter; a worked-out column's formula as the file writes it (`H+I`), its result and tree. */
      readonly letter?: string;
      readonly formula?: string;
      readonly result?: 'money' | 'number';
      readonly expr?: FormulaNode;
    })
  | (FindingBase<'link'> & { readonly from: string; readonly to: string; readonly how: 'pick' | 'lines' | 'lookup' })
  | (FindingBase<'number_watched'> & {
      readonly name: string;
      readonly over?: string;
      readonly how: 'sum' | 'count' | 'balance' | 'latest';
      /** A summary sheet's total (a card candidate): the data sheet it adds up, its conditions, and what it is by. */
      readonly sheet?: string;
      readonly when?: readonly FindingWhen[];
      readonly by?: string;
    })
  | (FindingBase<'money_flow'> & { readonly from: string; readonly to: string })
  | (FindingBase<'vocabulary'> & { readonly theirs: string; readonly means: string })
  /** Roles and how many hold them; never names. */
  | (FindingBase<'people_and_roles'> & { readonly role: string; readonly count?: number })
  /** Up to three rows, redacted. */
  | (FindingBase<'sample_rows'> & { readonly thing: string; readonly columns: readonly string[]; readonly rows: readonly (readonly string[])[] })
  | (FindingBase<'importable_data'> & {
      readonly thing: string;
      readonly shape: 'record' | 'list';
      readonly rows: number;
      readonly sheet?: string;
      /** A workbook map's kind of rows (`k1`): one sheet brings in each kind on its own (W8). */
      readonly rowKind?: string;
      readonly setAside?: number;
      /** The file has its own password: its rows are read again at import, the password asked again (step 4.6). */
      readonly locked?: true;
    })
  | (FindingBase<'question'> & { readonly question: string; readonly choices: readonly string[] })
  | (FindingBase<'conflict'> & {
      readonly about: string;
      readonly says: readonly { readonly sourceId: string | 'said'; readonly value: string }[];
      /** An index into `says`. */
      readonly likely?: number;
    })
  | (FindingBase<'cannot_do'> & { readonly want: string; readonly closest: string });

/** What a reader left out on purpose, and how many. */
export type LeftOut = {
  readonly kind: 'phone' | 'aadhaar' | 'pan' | 'card' | 'bank' | 'otp' | 'password' | 'column';
  readonly count: number;
  readonly where?: string;
};

/** Why a source was read only in part, and how much of it. */
export type SourcePartly = {
  readonly why: 'pages_skipped' | 'pages_later' | 'preview_only' | 'one_view' | 'part_shown' | 'pin_only' | 'no_words' | 'little_found';
  readonly n?: number;
  readonly total?: number;
  readonly skipped?: number;
};

/** What a source waits for from the person. */
export type SourceNeeds =
  | { readonly kind: 'password'; readonly tries: number }
  | { readonly kind: 'heard' }
  | { readonly kind: 'whose' }
  | { readonly kind: 'not_business' }
  | { readonly kind: 'private' }
  | { readonly kind: 'sign_in' };

/** Everything a source taught, as `app.agent_file.findings` keeps it. */
export type SourceFindings = {
  readonly v: 1;
  readonly items: readonly SourceFinding[];
  readonly leftOut: readonly LeftOut[];
  readonly partly: SourcePartly | null;
  readonly needs: SourceNeeds | null;
  /** Up to 600 characters: what the turn's context reads. */
  readonly digest: string;
  /** A bill's side. */
  readonly side?: 'sale' | 'purchase' | 'unknown';
  /** A screenshot of an app. */
  readonly screen?: boolean;
  /** Lines in the source that tried to instruct OS: dropped, and counted here, never kept. */
  readonly injected?: number;
  /**
   * The person's own site, read first (step 4.9): one or two sentences in their
   * voice from its business facts alone, to prefill the composer; null when the
   * site gave no name or trade.
   */
  readonly prewrite?: string | null;
  /**
   * A workbook's sheets past the first 20, not read, and the sheets OS is not
   * sure what they hold (`sheetsLeftOf`); absent when neither.
   */
  readonly sheetsLeft?: readonly SheetsLeftPart[];
  /**
   * Whether each sheet with rows holds data to bring in (F4 of
   * docs/plans/setup-file-data.md): one decision a sheet, with why. Absent on
   * findings written before it, and on a source whose rows never come in (one
   * the person likes, the app they use, a screenshot, a deck).
   */
  readonly data?: readonly SheetData[];
};

/**
 * Why a sheet is data or not: plain rules decide the clear cases (`table`: a
 * sheet with a header row and rows; `document`: a table inside a document);
 * `no_header` is a sheet the model could not judge, with no header row; the
 * model decides the rest (`model`, with its one sentence); the person has the
 * last word (`person`: Bring this in, or Not data).
 */
export const SHEET_DATA_WHYS = ['table', 'no_header', 'document', 'model', 'person'] as const;
export type SheetDataWhy = (typeof SHEET_DATA_WHYS)[number];

/** One sheet's decision: its rows to bring in, or not data. */
export type SheetData = {
  readonly sheet: string;
  readonly rows: number;
  readonly is: 'data' | 'not_data';
  readonly why: SheetDataWhy;
  /** The model's one sentence, with `why: 'model'`. */
  readonly note?: string;
  /** Where its rows go, as setup last planned them (step 2.2); absent until a draft holds somewhere for them. */
  readonly plan?: SheetPlan;
};

/**
 * Where a data sheet's rows go (step 2.2 of docs/plans/setup-file-data.md),
 * kept with the sheet so the column matcher and the model are asked again
 * only when the draft's parts the rows could go to change (`sig`).
 *
 *   planned  an import went into the draft for `record` (`seeded`: the list's
 *            names came from it too); taken out since, it is not planned again
 *   ask      the matcher and the model disagree, or neither is sure: the
 *            person picks one of `options` (a check with a chip each); a
 *            sheet whose rows mix kinds holds an option for each kind, all
 *            taken by one chip (W8 of docs/plans/workbook-map.md)
 *   none     nothing in the draft fits its rows yet
 */
export type SheetPlan = {
  readonly sig: string;
  readonly outcome: 'planned' | 'ask' | 'none';
  readonly record?: string;
  readonly seeded?: true;
  readonly options?: readonly Omit<StudioImport, 'key'>[];
  /** An ask's sheet rows: what its Leave out says it leaves out. Absent on a plan kept before. */
  readonly rows?: number;
};

/** A sheet's decision, or undefined when the findings hold none for it. */
export const sheetDataOf = (findings: Pick<SourceFindings, 'data'> | null | undefined, sheet: string | undefined): SheetData | undefined =>
  sheet === undefined ? undefined : findings?.data?.find((one) => one.sheet === sheet);

/**
 * Whether a sheet's rows count as data to bring in. Findings with no
 * decisions (written before them) count every sheet; a sheet the decisions
 * do not name counts; a finding that names no sheet counts while any sheet is data.
 */
export const isDataSheet = (findings: Pick<SourceFindings, 'data'> | null | undefined, sheet: string | undefined): boolean => {
  const data = findings?.data;
  if (data === undefined) return true;
  if (sheet === undefined) return data.length === 0 || data.some((one) => one.is === 'data');
  return (sheetDataOf(findings, sheet)?.is ?? 'data') === 'data';
};

/** The sheet a finding of rows names: its own, else where it was found. */
const rowsSheetOf = (one: SourceFinding): string | undefined => (one.kind === 'importable_data' ? one.sheet : undefined) ?? one.where.sheet;

/**
 * The findings that speak for a source, by its own verdict (F12 of
 * docs/plans/qa-run-1-review.md): a sheet judged not data says nothing of
 * its rows (no `importable_data`, no `sample_rows`) to the turn, the model or
 * the sources column; it is context. The store keeps them whole, so the
 * person's Bring this in counts them again.
 */
export const findingsByVerdict = <T extends SourceFinding>(findings: Pick<SourceFindings, 'data'> | null | undefined, items: readonly T[]): T[] =>
  items.filter((one) => (one.kind !== 'importable_data' && one.kind !== 'sample_rows') || isDataSheet(findings, rowsSheetOf(one)));

/**
 * Whether a source is context only (F12): it holds sheets with rows, and
 * every one was judged not data. It informs the setup; it is never where a
 * part's rows come from. A source with no decisions is not.
 */
export const sourceIsContext = (findings: Pick<SourceFindings, 'data'> | null | undefined): boolean => {
  const data = findings?.data ?? [];
  return data.length > 0 && data.every((one) => one.is === 'not_data');
};

/** The decisions after the person said so: the sheet named, or every sheet when none is. */
export const sheetDataSaid = (data: readonly SheetData[], said: { readonly sheet?: string; readonly is: SheetData['is'] }): SheetData[] =>
  data.map((one) => {
    if (said.sheet !== undefined && one.sheet !== said.sheet) return one;
    const { note: _note, ...rest } = one;
    return { ...rest, is: said.is, why: 'person' };
  });

/** The one state a source shows. The upload's percent stays on the client. */
export const sourceStateOf = (file: Pick<AgentFile, 'status' | 'failure' | 'findings' | 'leftAt'>): SourceState => {
  if (file.leftAt !== null) return 'left';
  switch (file.status) {
    case 'needs':
      return file.findings?.needs?.kind === 'heard' ? 'waiting' : 'needs';
    case 'pending':
      return 'uploading';
    case 'ready':
      return 'reading';
    case 'read':
      return file.findings !== null && file.findings.partly !== null ? 'partly' : 'read';
    case 'failed':
      return 'failed';
  }
};

/** The role a new source starts with: a video is one they like, a help page an app's, the rest their own. */
export const defaultRoleOf = (linkKind: LinkKind | null): SourceRole => {
  if (linkKind === 'video') return 'like';
  if (linkKind === 'help') return 'reference';
  return 'mine';
};

const SHAPE_ONLY: ReadonlySet<FindingKind> = new Set<FindingKind>(['thing_to_keep', 'field', 'link', 'number_watched', 'vocabulary', 'cannot_do']);

/**
 * The findings a role may hand the draft. `mine` keeps all; `like` and
 * `reference` keep only the shape (what is kept, its fields without their
 * samples or choice values, links, numbers watched, words, what cannot be
 * made), never a fact, a row or a person.
 */
export const findingsForRole = (items: readonly SourceFinding[], role: SourceRole): readonly SourceFinding[] => {
  if (role === 'mine') return items;
  return items
    .filter((one) => SHAPE_ONLY.has(one.kind))
    .map((one) => {
      if (one.kind !== 'field' || (one.sample === undefined && one.choices === undefined)) return one;
      const { sample: _sample, choices: _choices, ...rest } = one;
      return rest;
    });
};

/** One source as the conversation's column draws it. */
export type SetupSource = {
  readonly id: string;
  readonly name: string;
  readonly kind: FileKind;
  readonly linkKind: LinkKind | null;
  readonly role: SourceRole;
  readonly state: SourceState;
  readonly url: string | null;
  readonly failure: FileFailure | WebsiteFailure | null;
  readonly findings: SourceFindings | null;
  readonly pages: number | null;
  readonly createdAt: string;
};

/** A row as a source. A row with no stored role takes its link kind's default. */
export const setupSourceOf = (file: AgentFile): SetupSource => ({
  id: file.id,
  name: file.name,
  kind: file.kind,
  linkKind: file.linkKind,
  role: file.role ?? defaultRoleOf(file.linkKind),
  state: sourceStateOf(file),
  url: file.sourceUrl,
  failure: file.failure,
  findings: file.findings,
  pages: file.pages,
  createdAt: file.createdAt,
});

/** A source removed: what a `source` event on the session stream carries once its row is gone. */
export type SetupSourceGone = { readonly id: string; readonly deleted: true };

/** What a `source` event on the session stream carries: the source as it is now, or that it is gone. */
export type SetupSourceEvent = SetupSource | SetupSourceGone;

/* ------------------------------------------------------------------ routes */

/** GET /v1/setup/sessions/:id/sources: oldest first. */
export type SetupSourcesResponse = { readonly sources: readonly SetupSource[] };

/** PATCH /v1/setup/sessions/:id/sources/:sourceId. */
export type SetupSourcePatch = {
  readonly role?: SourceRole;
  readonly leave?: boolean;
  readonly keepPhones?: boolean;
  readonly heard?: 'read' | 'not_this';
  /** Bring this in, or Not data (F4): one sheet, or every sheet of the source when none is named. */
  readonly data?: { readonly sheet?: string; readonly is: SheetData['is'] };
};

export type SetupSourceResponse = { readonly source: SetupSource };

/** A file's own password, used once in memory, never stored or logged. */
export type SourceUnlockRequest = { readonly password: string };

/** A password is 1 to this many characters. */
export const SOURCE_PASSWORD_MAX = 128;

/** 422 WRONG_PASSWORD: the tries so far; at the third the source has failed with `password`. */
export type SourceUnlockRefused = { readonly error: { readonly code: 'WRONG_PASSWORD'; readonly message: string }; readonly tries: number };

/** A links request holds 1 to this many entries, each at most `SETUP_LINK_MAX` characters. */
export const SETUP_LINKS_MAX = 50;
export const SETUP_LINK_MAX = 500;

/** Links pasted or heard, each read as its own source. */
export type SetupLinksRequest = {
  readonly urls: readonly string[];
  readonly heard?: boolean;
  readonly roles?: Readonly<Record<string, SourceRole>>;
};

export type SetupLinksResponse = {
  readonly sources: readonly SetupSource[];
  /** What was not read as a link: an app's name (for `research_app`), a phone or a handle (kept as a fact), or nothing to read. */
  readonly refused: readonly { readonly text: string; readonly why: 'not_a_link' | 'phone' | 'handle' | 'app' }[];
};

/**
 * GET /v1/setup/sessions/:id/sources/:sourceId/read (step 5.13b): what OS
 * read, redacted as the source's role allows. A scan's table with how sure
 * each cell is; a sheet's or a page's lines; a screenshot's items, each with
 * its card or not possible yet; a photo's short-lived download link.
 */
export type SetupSourceRead = {
  readonly table?: { readonly sheet: string; readonly columns: readonly string[]; readonly rows: readonly { readonly cells: readonly string[]; readonly sure: readonly number[] }[] };
  readonly lines?: readonly string[];
  readonly screen?: { readonly items: readonly { readonly text: string; readonly card: string | null; readonly notYet: boolean }[] };
  readonly photoUrl?: string;
};

/** PATCH …/sources/:sourceId/cell: Fix a word, one cell of a scan's table written as the person read it. */
export type SetupSourceCellPatch = { readonly sheet: string; readonly row: number; readonly column: number; readonly value: string };

/** A fixed word is at most this many characters. */
export const SOURCE_CELL_MAX = 500;

/* --------------------------------------------------------- business details */

/** Where a setup detail candidate was heard: the person's words, or one of their own files or sites. */
export const SETUP_DETAIL_FROMS = ['said', 'file', 'website'] as const;
export type SetupDetailFrom = (typeof SETUP_DETAIL_FROMS)[number];

/**
 * A business detail heard in setup (D14 of docs/plans/report-templates.md),
 * held in `app.setup_session.details` until Build, never in the store. Only
 * from the person's words and from `mine` sources. `slot` is null for a new
 * value of a multi-valued key (a phone): Build gives it a free one.
 */
export type SetupDetailCandidate = {
  readonly id: string;
  readonly key: string;
  readonly slot: string | null;
  readonly value: string;
  readonly from: SetupDetailFrom;
  /** The source it was read from; null when the person said it. */
  readonly sourceId: string | null;
};

/** GET /v1/setup/sessions/:id/details: the candidates the Build sheet lists, oldest first. */
export type SetupDetailsResponse = { readonly details: readonly SetupDetailCandidate[] };
